import React, { useEffect, useMemo, useRef, useState } from "react";
import type { Contact, Deal, Partner, TaskAi, TaskDraft, Todo } from "../types";
import { useData } from "../hooks/useData";
import { api } from "../api/client";
import { I } from "./Icons";
import { Avatar, AvatarStack, clsx, fmtDate } from "./Primitives";

/* ---- shared helpers (also used by the list) ---- */

export const todayIso = () => new Date().toLocaleDateString("sv-SE");
export const plusDays = (n: number, from = todayIso()) => {
  const d = new Date(from + "T00:00:00");
  d.setDate(d.getDate() + n);
  return d.toLocaleDateString("sv-SE");
};
export const nextMonday = () => {
  const d = new Date(todayIso() + "T00:00:00");
  d.setDate(d.getDate() + (((8 - d.getDay()) % 7) || 7));
  return d.toLocaleDateString("sv-SE");
};
export const firstName = (partners: Record<string, Partner>, id: string | null | undefined) =>
  !id ? "—" : id === "all" ? "Everyone" : id === "wait" ? "Waiting" : partners[id]?.name.split(" ")[0] || id.replace(/^bot:/, "");
/** Title without a leading "F15: " — the code is shown as its own chip. */
export const cleanTitle = (t: Todo) =>
  t.external_code && t.task.startsWith(t.external_code) ? t.task.slice(t.external_code.length).replace(/^\s*[:–-]\s*/, "") : t.task;
/** "F9–F11/F40" → F9, F10, F11, F40 */
export function codeTokens(code: string | null | undefined): string[] {
  if (!code) return [];
  const out: string[] = [];
  for (const part of code.split("/")) {
    const r = part.replace(/\s+/g, "").match(/^([FS])(\d{1,2})(?:[–-]([FS])?(\d{1,2}))?$/);
    if (!r) continue;
    const a = +r[2], b = r[4] ? +r[4] : a;
    for (let n = a; n <= Math.min(b, a + 20); n++) out.push(`${r[1]}${n}`);
  }
  return out;
}
export const KIND_LABEL: Record<string, string> = {
  send: "Send / write", chase: "Confirm / chase", ask: "Ask for info", meeting: "Meeting", research: "Research",
  decision: "Decision", admin: "Admin", monitor: "Monitor",
};
const DRAFT_KINDS = ["send", "chase", "ask", "meeting"];

interface Ctx {
  task: Todo;
  deal: { id: string; name: string; stage: string; owner: string; owner_name: string | null; next_step: string | null; next_due: string | null;
    market: string | null; principal: string | null; counterparty: string | null } | null;
  contacts: { id: string; name: string; email: string | null; phone: string | null; title: string | null; org_name: string | null; email_suggestions?: AddressGuess[] }[];
  emails: { id: string; date: string; from_name: string | null; from_email: string | null; subject: string | null; preview: string | null; outgoing: boolean }[];
  minutes: { id: string; title: string; date: string; summary: string | null }[];
  siblings: { id: string; task: string; owner: string; status: string; due: string | null; external_code: string | null }[];
  children: { id: string; task: string; owner: string; status: string; due: string | null; external_code: string | null }[];
  parent: { id: string; task: string; status: string } | null;
  blocked_by: { id: string; task: string; status: string; owner: string; external_code: string | null }[];
  blocks: { id: string; task: string; status: string; owner: string; external_code: string | null }[];
  history: { at: string; by: string | null; by_name: string | null; field: string; old_value: string | null; new_value: string | null }[];
  source: { type: string; label: string | null; url?: string | null; id?: string | null } | null;
  gaps: string[];
  reply: { id: string; from_name: string | null; from_email: string | null; subject: string | null; preview: string | null; date: string } | null;
}
interface AddressGuess { email: string; count: number; last_seen: string | null; how: string; taken_by?: string | null }
interface Verify {
  sent: { id: string; date: string; subject: string | null; to_addresses: string[] | null; from_name: string | null }[];
  received: { id: string; date: string; subject: string | null; from_name: string | null }[];
  searched: { addresses: string[]; words: string[] };
  mailboxes: { id: string; name: string; connected: boolean; last_email: string | null }[];
}

type Pop = null | "waiting" | "snooze" | "delegate" | "more";

export default function TaskPanel({
  id, partners, deals, contacts, go, onClose, onChanged, onStep, myId,
}: {
  id: string;
  partners: Record<string, Partner>;
  deals: Deal[];
  contacts: Contact[];
  go: (screen: string, id?: string) => void;
  onClose: () => void;
  onChanged: () => void;
  onStep: (dir: 1 | -1) => void;
  myId: string;
}) {
  const { data: ctx, refetch, setData } = useData<Ctx>(`/api/todos/${id}/context`);
  const [pop, setPop] = useState<Pop>(null);
  const [busy, setBusy] = useState<string | null>(null);
  const [err, setErr] = useState<string | null>(null);
  const [ok, setOk] = useState<string | null>(null);
  const [editing, setEditing] = useState<null | "title" | "note">(null);
  const [verify, setVerify] = useState<Verify | null>(null);
  const [splitText, setSplitText] = useState<string | null>(null);
  const [draft, setDraft] = useState<TaskDraft | null>(null);
  const [instr, setInstr] = useState("");
  const [afterSend, setAfterSend] = useState(false);
  const [showHistory, setShowHistory] = useState(false);
  const bodyRef = useRef<HTMLDivElement>(null);
  const t = ctx?.task.id === id ? ctx.task : null;

  // Fresh state for each task.
  useEffect(() => {
    setPop(null); setErr(null); setOk(null); setEditing(null); setVerify(null); setSplitText(null);
    setAfterSend(false); setInstr(""); setShowHistory(false);
    bodyRef.current?.scrollTo(0, 0);
  }, [id]);
  useEffect(() => { setDraft(t?.ai?.draft ? { ...t.ai.draft } : null); }, [t?.id, t?.ai?.generated_at]);

  const run = async (label: string, fn: () => Promise<unknown>, done?: string) => {
    setBusy(label); setErr(null); setOk(null);
    try { await fn(); if (done) setOk(done); } catch (e) { setErr(e instanceof Error ? e.message : String(e)); }
    finally { setBusy(null); }
  };
  const save = (patch: Partial<Todo> & Record<string, unknown>, done?: string) => run("save", async () => {
    const updated = await api.put<Todo>(`/api/todos/${id}`, patch);
    setData(c => (c ? { ...c, task: { ...c.task, ...updated } } : c));
    refetch(); onChanged();
  }, done);

  const complete = (evidence?: string) => save({ status: "done", ...(evidence ? { done_evidence: evidence } : {}) }, "Done ✓");
  const reopen = () => save({ status: "open" }, "Reopened");

  // Keyboard: E complete · W waiting · S snooze · D delegate · ↑↓ next task · Esc close
  useEffect(() => {
    const h = (e: KeyboardEvent) => {
      const el = e.target as HTMLElement;
      if (el && (el.tagName === "INPUT" || el.tagName === "TEXTAREA" || el.tagName === "SELECT" || el.isContentEditable)) return;
      if (e.metaKey || e.ctrlKey || e.altKey) return;
      if (e.key === "Escape") { if (pop) setPop(null); else onClose(); return; }
      if (e.key === "ArrowDown" || e.key === "j") { e.preventDefault(); onStep(1); return; }
      if (e.key === "ArrowUp" || e.key === "k") { e.preventDefault(); onStep(-1); return; }
      if (!t) return;
      const k = e.key.toLowerCase();
      if (k === "e" && t.status !== "done") { e.preventDefault(); complete(); }
      else if (k === "w") { e.preventDefault(); setPop("waiting"); }
      else if (k === "s") { e.preventDefault(); setPop("snooze"); }
      else if (k === "d") { e.preventDefault(); setPop("delegate"); }
    };
    window.addEventListener("keydown", h);
    return () => window.removeEventListener("keydown", h);
  });

  const dealOptions = useMemo(() => [...deals].sort((a, b) => a.name.localeCompare(b.name)), [deals]);

  if (!t || !ctx) {
    return (
      <aside className="tp">
        <div className="tp-top"><span className="tp-crumb">Tasks</span><button className="icon-btn" onClick={onClose} aria-label="Close">✕</button></div>
        <div className="tp-loading">Loading…</div>
      </aside>
    );
  }

  const ai: TaskAi | null = t.ai || null;
  const closed = t.status === "done" || t.status === "dropped";
  const canDraft = DRAFT_KINDS.includes(t.kind || "") || !!ai?.draft;
  const proposed = t.due && t.due_confidence === "proposed";
  const overdue = t.due && t.due < todayIso() && t.status === "open";
  const sendDraft = async () => {
    if (!draft) return;
    if (!draft.to.length) { setErr("Add who it goes to"); return; }
    if (!window.confirm(`Send “${draft.subject}” to ${draft.to.join(", ")} from your Gmail?`)) return;
    const contactAddrs = ctx.contacts.map(c => (c.email || "").toLowerCase());
    const lastFromThem = ctx.emails.find(e => !e.outgoing && contactAddrs.includes((e.from_email || "").toLowerCase()));
    await run("send", async () => {
      await api.post("/api/emails/send", {
        to: draft.to.join(", "), cc: draft.cc, subject: draft.subject, body: draft.body, task_id: id,
        contact_id: ctx.contacts[0]?.id, ...(lastFromThem && /^re:/i.test(draft.subject) ? { reply_to_email_id: lastFromThem.id } : {}),
      });
      setAfterSend(true); refetch();
    }, "Sent from your Gmail ✓");
  };
  const mailto = draft
    ? `mailto:${encodeURIComponent(draft.to.join(","))}?${new URLSearchParams({ ...(draft.cc.length ? { cc: draft.cc.join(",") } : {}), subject: draft.subject, body: draft.body }).toString().replace(/\+/g, "%20")}`
    : "";
  const crumb = ctx.deal?.name || t.principal_name || "Tasks";

  return (
    <aside className="tp" aria-label="Task">
      <div className="tp-top">
        <span className="tp-crumb">Tasks <span className="tp-crumb-sep">›</span> {crumb}</span>
        <span style={{ flex: 1 }} />
        <div className="tp-pop-anchor">
          <button className="icon-btn" title="More" onClick={() => setPop(pop === "more" ? null : "more")}>⋯</button>
          {pop === "more" && (
            <div className="tp-pop right">
              {!t.parent_id && !closed && <button onClick={() => {
                setPop(null);
                const codes = codeTokens(t.external_code);
                setSplitText(codes.length > 1 ? codes.map(c => `${c}: `).join("\n") : "\n");
              }}>Split into sub-tasks</button>}
              {t.status !== "monitor" && !closed && <button onClick={() => { setPop(null); save({ status: "monitor", kind: "monitor" }, "Moved to Monitoring"); }}>Make it a watch (Monitoring)</button>}
              {t.status === "monitor" && <button onClick={() => { setPop(null); save({ status: "open" }, "Back on the to-do list"); }}>Make it a to-do again</button>}
              {!closed && <button onClick={() => {
                setPop(null);
                const reason = window.prompt("Why drop it? (kept in the history)", "Not needed any more");
                if (reason) save({ status: "dropped", dropped_reason: reason }, "Dropped");
              }}>Drop…</button>}
              <button className="danger" onClick={async () => {
                setPop(null);
                if (!window.confirm("Delete this task for good? Dropping keeps it in the history instead.")) return;
                await api.delete(`/api/todos/${id}`); onChanged(); onClose();
              }}>Delete</button>
            </div>
          )}
        </div>
        <button className="icon-btn" onClick={onClose} aria-label="Close" title="Close (Esc)">✕</button>
      </div>

      <div className="tp-body" ref={bodyRef}>
        {ctx.parent && (
          <button className="tp-parent" onClick={() => go("tasks", ctx.parent!.id)}>↳ Part of: {ctx.parent.task}</button>
        )}
        {editing === "title" ? (
          <textarea className="tp-title-edit" autoFocus defaultValue={t.task} rows={2}
            onBlur={e => { setEditing(null); if (e.target.value.trim() && e.target.value !== t.task) save({ task: e.target.value.trim() }); }}
            onKeyDown={e => { if (e.key === "Enter" && !e.shiftKey) { e.preventDefault(); (e.target as HTMLTextAreaElement).blur(); } }} />
        ) : (
          <h2 className={clsx("tp-title", closed && "closed")} title="Click to edit" onClick={() => setEditing("title")}>{cleanTitle(t)}</h2>
        )}

        <div className="tp-chips">
          {t.principal_name && <span className="tk-chip"><i className="tk-dot" />{t.principal_name}</span>}
          {t.external_code && <span className="tk-code">{t.external_code}</span>}
          {t.kind && <span className="tk-chip soft">{KIND_LABEL[t.kind]}</span>}
          {t.status === "waiting" && <span className="tk-chip wait">⏳ Waiting on {t.waiting_on}{t.chase_on ? ` · chase ${fmtDate(t.chase_on)}` : ""}</span>}
          {t.status === "monitor" && <span className="tk-chip soft">◎ Monitoring</span>}
          {t.status === "done" && <span className="tk-chip ok">✓ Done{t.done_at ? ` ${fmtDate(t.done_at.slice(0, 10))}` : ""}</span>}
          {t.status === "dropped" && <span className="tk-chip soft">Dropped — {t.dropped_reason}</span>}
          {t.suggested && <span className="tk-chip sug">✦ Suggested</span>}
          {ai?.draft && !ai.stale && t.status === "open" && <span className="tk-chip ai">✦ Draft ready</span>}
          {t.snoozed_until && t.snoozed_until > todayIso() && <span className="tk-chip soft">Snoozed to {fmtDate(t.snoozed_until)}</span>}
        </div>

        <div className="tp-meta">
          <label className={clsx("tp-meta-item", overdue && "late")}>
            <span className="tp-k">Due</span>
            <input type="date" className="tp-date mono" value={t.due || ""} onChange={e => save({ due: e.target.value || null })} />
            {proposed && (
              <button className="tp-proposed" title="Proposed by AI — click to confirm it's agreed" onClick={() => save({ due_confidence: "agreed" }, "Date confirmed")}>
                proposed · agree
              </button>
            )}
          </label>
          <div className="tp-meta-item tp-pop-anchor">
            <span className="tp-k">Owner</span>
            <button className="tp-owner" onClick={() => setPop(pop === "delegate" ? null : "delegate")} title="Delegate (D)">
              <Avatar partner={partners[t.owner] || null} size={22} />{firstName(partners, t.owner)}
            </button>
            {(t.co_owners || []).length > 0 && <span className="tp-co">+ {(t.co_owners || []).map(c => firstName(partners, c)).join(", ")}</span>}
            {pop === "delegate" && (
              <div className="tp-pop">
                <div className="tp-pop-h">Give it to</div>
                {Object.values(partners).filter(p => p.active !== false).map(p => (
                  <button key={p.id} className={clsx(p.id === t.owner && "on")} onClick={() => { setPop(null); save({ owner: p.id }, `Handed to ${p.name.split(" ")[0]}`); }}>
                    <Avatar partner={p} size={18} />{p.name}
                  </button>
                ))}
                <button onClick={() => { setPop(null); save({ owner: "all" }, "Shared with everyone"); }}>Everyone</button>
                <div className="tp-pop-h">Also working on it</div>
                <div className="tp-pop-row">
                  {Object.values(partners).filter(p => p.active !== false && p.id !== t.owner).map(p => {
                    const on = (t.co_owners || []).includes(p.id);
                    return <button key={p.id} className={clsx("tp-mini", on && "on")}
                      onClick={() => save({ co_owners: on ? (t.co_owners || []).filter(x => x !== p.id) : [...(t.co_owners || []), p.id] })}>{p.name.split(" ")[0]}</button>;
                  })}
                </div>
              </div>
            )}
          </div>
          <label className="tp-meta-item">
            <span className="tp-k">Priority</span>
            <select className="tp-select" value={t.priority} onChange={e => save({ priority: e.target.value as Todo["priority"] })}>
              <option value="high">High</option><option value="med">Normal</option><option value="low">Low</option>
            </select>
          </label>
          <label className="tp-meta-item">
            <span className="tp-k">Type</span>
            <select className="tp-select" value={t.kind || ""} onChange={e => save({ kind: (e.target.value || null) as Todo["kind"] })}>
              <option value="">—</option>
              {Object.entries(KIND_LABEL).map(([k, v]) => <option key={k} value={k}>{v}</option>)}
            </select>
          </label>
        </div>

        <WithRow ctx={ctx} t={t} contacts={contacts} onSave={save} go={go}
          onDismiss={(kind, key, label) => run("save", async () => {
            await api.post("/api/dismissals", { kind, key, label });
            refetch();
          }, "Won't be suggested again")}
          onFix={f => run("save", async () => {
            await api.post("/api/todos/hygiene/fix", { action: f.action, task_ids: [id], payload: f.payload });
            refetch(); onChanged();
          }, "Saved — undo it from the check-up's “Changes today” if it's wrong")} />

        {ctx.gaps.length > 0 && !closed && (
          <ul className="tp-gaps">{ctx.gaps.map(g => <li key={g}>{g}</li>)}</ul>
        )}

        {(err || ok) && <div className={clsx("tp-notice", err ? "err" : "ok")}>{err || ok}</div>}

        {ctx.reply && t.status === "waiting" && (
          <div className="tp-reply">
            <div className="tp-reply-h">✉ Reply received from {ctx.reply.from_name || ctx.reply.from_email} · {fmtDate(String(ctx.reply.date).slice(0, 10))}</div>
            <button className="tp-reply-subj" onClick={() => go("inbox", ctx.reply!.id)}>{ctx.reply.subject || "(no subject)"}</button>
            {ctx.reply.preview && <div className="tp-reply-prev">{ctx.reply.preview.slice(0, 220)}</div>}
            {t.reply_proposal && (
              <div className="tp-reply-prop">🤖 {String(t.reply_proposed_by || "A bot").replace(/^bot:/, "")} thinks
                {t.reply_proposal === "done" ? " this answers it" : " this isn't the answer"} — your call.</div>
            )}
            <div className="tp-row-btns">
              <button className="btn primary sm" onClick={() => run("save", async () => {
                await api.post(`/api/todos/${id}/reply`, { action: "done" }); refetch(); onChanged();
              }, "Done — the reply is the evidence ✓")}>That answers it — mark done</button>
              <button className="btn ghost sm" onClick={() => run("save", async () => {
                await api.post(`/api/todos/${id}/reply`, { action: "dismiss" }); refetch(); onChanged();
              }, "Still waiting — later replies will be checked too")}>Not the answer — keep waiting</button>
            </div>
          </div>
        )}

        {t.handoff === "pending" && (
          <HandoffBanner t={t} partners={partners} myId={myId} onAnswer={(accept, note) => run("save", async () => {
            await api.post(`/api/todos/${id}/handoff`, { accept, note }); refetch(); onChanged();
          }, accept ? "On your list ✓" : "Handed back")} />
        )}

        {afterSend && (
          <div className="tp-after">
            <span>Sent. What now?</span>
            <button className="btn primary sm" onClick={() => { setAfterSend(false); complete(`Email sent: ${draft?.subject || ""}`); }}>Mark done</button>
            <button className="btn ghost sm" onClick={() => {
              setAfterSend(false);
              const who = ctx.contacts[0]?.name || t.with_text || "reply";
              save({ status: "waiting", waiting_on: who, chase_on: plusDays(7) }, `Waiting on ${who} — back on ${fmtDate(plusDays(7))}`);
            }}>Wait for the reply (chase in 7 days)</button>
          </div>
        )}

        {/* ---- context ---- */}
        <section className="tp-sec">
          <div className="tp-h">Context</div>
          {editing === "note" ? (
            <textarea className="tp-note-edit" autoFocus defaultValue={t.note || ""} rows={8}
              onBlur={e => { setEditing(null); if (e.target.value !== (t.note || "")) save({ note: e.target.value }); }} />
          ) : (
            <div className="tp-note" onClick={() => setEditing("note")} title="Click to edit">
              {t.note ? t.note.split(/\n{2,}/).map((p, i) => <p key={i}>{p}</p>) : <span className="tp-empty">No notes yet — click to add.</span>}
            </div>
          )}
          {ctx.deal?.next_step && (
            <div className="tp-dealnext"><span className="tp-k">Deal next step</span> {ctx.deal.next_step}{ctx.deal.next_due ? ` · ${fmtDate(ctx.deal.next_due)}` : ""}</div>
          )}
        </section>

        {/* ---- sources ---- */}
        <section className="tp-sec">
          <div className="tp-h">Sources</div>
          <div className="tp-sources">
            {ctx.deal ? (
              <button className="tp-src" onClick={() => go("project", ctx.deal!.id)}>◆ Deal: {ctx.deal.name}
                {ctx.deal.owner_name && ctx.deal.owner !== myId && <span className="tp-src-sub"> · {ctx.deal.owner_name.split(" ")[0]}'s</span>}</button>
            ) : null}
            <select className="tp-deal-pick" value={t.deal_id || ""} onChange={e => save({ deal_id: e.target.value || null }, e.target.value ? "Linked to the deal" : "Unlinked")}>
              <option value="">{t.deal_id ? "No deal" : "+ Link a deal…"}</option>
              {dealOptions.map(d => <option key={d.id} value={d.id}>{d.name}</option>)}
            </select>
            {ctx.source && <SourceChip src={ctx.source} go={go} />}
            {ctx.minutes.map(m => (
              <a key={m.id} className="tp-src" href={`/api/minutes/${m.id}/doc`} target="_blank" rel="noreferrer">▤ Minutes: {m.title} · {fmtDate(m.date)}</a>
            ))}
          </div>
          {ctx.emails.length > 0 && (
            <div className="tp-mails">
              {ctx.emails.slice(0, 4).map(e => (
                <button key={e.id} className="tp-mail" onClick={() => go("inbox", e.id)} title="Open in Inbox">
                  <span className={clsx("tp-mail-dir", e.outgoing && "out")}>{e.outgoing ? "Sent" : "In"}</span>
                  <span className="tp-mail-subj">{e.subject || "(no subject)"}</span>
                  <span className="tp-mail-who">{e.outgoing ? "" : e.from_name}</span>
                  <span className="tp-mail-date mono">{fmtDate(String(e.date).slice(0, 10))}</span>
                </button>
              ))}
            </div>
          )}
        </section>

        {/* ---- dependencies & sub-tasks ---- */}
        {(ctx.blocked_by.length > 0 || ctx.blocks.length > 0) && (
          <section className="tp-sec">
            <div className="tp-h">Dependencies</div>
            {ctx.blocked_by.map(b => (
              <button key={b.id} className="tp-link-row" onClick={() => go("tasks", b.id)}>
                <span className={clsx("tp-dep", ["done", "dropped"].includes(b.status) ? "ok" : "wait")}>{["done", "dropped"].includes(b.status) ? "✓" : "⏳"}</span>
                Waits on {b.external_code ? <b>{b.external_code}</b> : null} {b.task.replace(/^[FS]\d[^:]*:\s*/, "")} · {firstName(partners, b.owner)}
              </button>
            ))}
            {ctx.blocks.map(b => (
              <button key={b.id} className="tp-link-row" onClick={() => go("tasks", b.id)}>
                <span className="tp-dep">→</span> Holds up {b.external_code ? <b>{b.external_code}</b> : null} {b.task.replace(/^[FS]\d[^:]*:\s*/, "")} · {firstName(partners, b.owner)}
              </button>
            ))}
          </section>
        )}

        {(ctx.children.length > 0 || splitText !== null) && (
          <section className="tp-sec">
            <div className="tp-h">Sub-tasks {ctx.children.length > 0 && <span className="tp-h-n">{ctx.children.filter(c => ["done", "dropped"].includes(c.status)).length}/{ctx.children.length}</span>}</div>
            {ctx.children.map(c => (
              <div key={c.id} className="tp-child">
                <button className={clsx("checkbox", c.status === "done" && "checked")} aria-label="Done"
                  onClick={async () => { await api.put(`/api/todos/${c.id}`, { status: c.status === "done" ? "open" : "done" }); refetch(); onChanged(); }}>
                  {c.status === "done" && I.check({ size: 12, w: 2.4 })}
                </button>
                <button className={clsx("tp-child-t", ["done", "dropped"].includes(c.status) && "closed")} onClick={() => go("tasks", c.id)}>{c.task}</button>
                <span className="tp-child-m mono">{c.due ? fmtDate(c.due) : ""} · {firstName(partners, c.owner)}</span>
              </div>
            ))}
            {splitText !== null && (
              <div className="tp-split">
                <div className="tp-hint">One sub-task per line. Each gets its own date and status; the task is done when they all are.</div>
                <textarea rows={Math.max(4, splitText.split("\n").length + 1)} value={splitText} onChange={e => setSplitText(e.target.value)} autoFocus />
                <div className="tp-row-btns">
                  <button className="btn ghost sm" onClick={() => setSplitText(null)}>Cancel</button>
                  <button className="btn primary sm" disabled={busy === "split"} onClick={() => run("split", async () => {
                    const items = splitText.split("\n").map(s => s.trim()).filter(s => s.replace(/^[FS]\d{1,2}:\s*$/, ""));
                    await api.post(`/api/todos/${id}/split`, { items });
                    setSplitText(null); refetch(); onChanged();
                  }, "Split ✓")}>Split</button>
                </div>
              </div>
            )}
          </section>
        )}

        {/* ---- AI ---- */}
        {!closed && (
          <section className="tp-sec">
            <div className="tp-h">✦ Assistant {ai?.generated_at && <span className="tp-h-sub">{ai.stale ? "out of date — " : ""}prepared {fmtDate(ai.generated_at.slice(0, 10))}</span>}</div>
            {!ai && (
              <div className="tp-ai-empty">
                <p>{canDraft ? "Let the assistant draft the email in the counterpart's language, from the deal, the contacts and the last thread — you review and send."
                  : "Let the assistant suggest next steps and write a short brief from what the CRM knows."}</p>
                <button className="btn primary sm" disabled={busy === "prepare"} onClick={() => run("prepare", async () => {
                  await api.post(`/api/todos/${id}/prepare`, {}); refetch(); onChanged();
                })}>{busy === "prepare" ? "Preparing…" : canDraft ? "✦ Prepare a draft" : "✦ Prepare"}</button>
              </div>
            )}
            {ai && (
              <>
                {ai.suggestions.length > 0 && (
                  <>
                    <div className="tp-sub-h">Suggestions</div>
                    <ul className="tp-sugg">{ai.suggestions.map(s => <li key={s}>{s}</li>)}</ul>
                  </>
                )}
                {ai.missing.length > 0 && (
                  <>
                    <div className="tp-sub-h">You'll need</div>
                    <ul className="tp-missing">{ai.missing.map(s => <li key={s}>{s}</li>)}</ul>
                  </>
                )}
                {ai.brief && (
                  <>
                    <div className="tp-sub-h">Brief</div>
                    <div className="tp-brief">{ai.brief}</div>
                  </>
                )}
                {draft && (
                  <>
                    <div className="tp-sub-h">Draft</div>
                    <div className="tp-draft">
                      <div className="tp-draft-top"><span className="tp-draft-tag">DRAFT</span> Not sent{draft.language ? ` · ${draft.language.toUpperCase()}` : ""}</div>
                      <label className="tp-draft-f"><span>To</span>
                        <input value={draft.to.join(", ")} placeholder="name@company.com"
                          onChange={e => setDraft({ ...draft, to: e.target.value.split(/[,;]\s*/).filter(Boolean) })} /></label>
                      <label className="tp-draft-f"><span>Cc</span>
                        <input value={draft.cc.join(", ")} onChange={e => setDraft({ ...draft, cc: e.target.value.split(/[,;]\s*/).filter(Boolean) })} /></label>
                      <label className="tp-draft-f"><span>Subject</span>
                        <input value={draft.subject} onChange={e => setDraft({ ...draft, subject: e.target.value })} /></label>
                      <textarea className="tp-draft-body" rows={Math.min(18, Math.max(8, draft.body.split("\n").length + 2))}
                        value={draft.body} onChange={e => setDraft({ ...draft, body: e.target.value })} />
                      <div className="tp-row-btns">
                        <button className="btn primary sm" disabled={busy === "send"} onClick={sendDraft}>{I.send({ size: 13 })} {busy === "send" ? "Sending…" : "Send from Gmail"}</button>
                        <a className="btn ghost sm" href={mailto}>Open in email ↗</a>
                        <button className="btn ghost sm" onClick={() => navigator.clipboard?.writeText(`${draft.subject}\n\n${draft.body}`).then(() => setOk("Copied"))}>Copy</button>
                      </div>
                    </div>
                  </>
                )}
                <div className="tp-regen">
                  <input value={instr} placeholder={canDraft ? "Tell the assistant what to change (e.g. “shorter, in Portuguese, propose Teams on Thursday”)" : "Anything to focus on?"}
                    onChange={e => setInstr(e.target.value)}
                    onKeyDown={e => { if (e.key === "Enter") (e.currentTarget.nextSibling as HTMLButtonElement)?.click(); }} />
                  <button className="btn ghost sm" disabled={busy === "prepare"} onClick={() => run("prepare", async () => {
                    await api.post(`/api/todos/${id}/prepare`, { instructions: instr || null }); setInstr(""); refetch(); onChanged();
                  })}>↻ {busy === "prepare" ? "Working…" : "Regenerate"}</button>
                </div>
              </>
            )}
          </section>
        )}

        {/* ---- proof ---- */}
        {!closed && (
          <section className="tp-sec">
            <div className="tp-h">Did it happen?</div>
            {!verify ? (
              <button className="btn ghost sm" disabled={busy === "verify"} onClick={() => run("verify", async () => {
                setVerify(await api.post<Verify>(`/api/todos/${id}/verify`, {}));
              })}>{busy === "verify" ? "Searching…" : "Check the mailboxes for proof"}</button>
            ) : (
              <div className="tp-verify">
                <div className="tp-mailboxes">
                  Searched {verify.mailboxes.filter(m => m.connected).map(m => `${m.name.split(" ")[0]}'s`).join(", ") || "no"} mailbox{verify.mailboxes.filter(m => m.connected).length === 1 ? "" : "es"}
                  {verify.mailboxes.some(m => !m.connected) && (
                    <span className="tp-mailboxes-miss"> · not connected: {verify.mailboxes.filter(m => !m.connected).map(m => m.name.split(" ")[0]).join(", ")} — their email isn't in the CRM</span>
                  )}
                </div>
                {verify.sent.length === 0 && verify.received.length === 0 && (
                  <p className="tp-empty">Nothing found in the synced mailboxes{verify.searched.addresses.length ? ` to or from ${verify.searched.addresses.join(", ")}` : ""}.
                    {canDraft ? " Prepare a chaser above, or it was sent from a mailbox the CRM doesn't sync." : ""}</p>
                )}
                {[...verify.sent.map(e => ({ ...e, dir: "Sent" })), ...verify.received.map(e => ({ ...e, dir: "Reply" }))].map(e => (
                  <div key={e.id} className="tp-proof">
                    <span className={clsx("tp-mail-dir", e.dir === "Sent" && "out")}>{e.dir}</span>
                    <button className="tp-mail-subj" onClick={() => go("inbox", e.id)}>{e.subject || "(no subject)"}</button>
                    <span className="tp-mail-date mono">{fmtDate(String(e.date).slice(0, 10))}</span>
                    <button className="btn ghost sm" onClick={() => complete(`${e.dir === "Sent" ? "Sent" : "Answered"}: ${e.subject} (${String(e.date).slice(0, 10)})`)}>Done — this proves it</button>
                  </div>
                ))}
              </div>
            )}
          </section>
        )}
        {t.done_evidence && <section className="tp-sec"><div className="tp-h">Evidence</div><div className="tp-note">{t.done_evidence}</div></section>}

        {ctx.siblings.length > 0 && (
          <section className="tp-sec">
            <div className="tp-h">Also open on {ctx.deal?.name}</div>
            {ctx.siblings.map(s => (
              <button key={s.id} className="tp-link-row" onClick={() => go("tasks", s.id)}>
                {s.external_code && <b>{s.external_code}</b>} {s.task.replace(/^[FS]\d[^:]*:\s*/, "")}
                <span className="tp-link-m">{s.status === "waiting" ? "waiting · " : ""}{s.due ? fmtDate(s.due) : ""} · {firstName(partners, s.owner)}</span>
              </button>
            ))}
          </section>
        )}

        <section className="tp-sec">
          <button className="tp-h tp-h-btn" onClick={() => setShowHistory(v => !v)}>History {showHistory ? "▾" : "▸"} <span className="tp-h-n">{ctx.history.length}</span></button>
          {showHistory && (
            <ul className="tp-history">
              {ctx.history.map((h, i) => <li key={i}><span className="mono">{fmtDate(h.at.slice(0, 10))}</span> {historyLine(h, partners)}</li>)}
            </ul>
          )}
        </section>
      </div>

      {/* ---- actions ---- */}
      <div className="tp-foot">
        {t.suggested ? (
          <SuggestedActions t={t} partners={partners} onAccept={(owner, due) => run("save", async () => {
            await api.post(`/api/todos/${id}/accept`, { owner, due }); refetch(); onChanged();
          }, "On the list ✓")} onDismiss={() => save({ status: "dropped", dropped_reason: "Suggestion not taken up", suggested: false }, "Dismissed")} />
        ) : closed ? (
          <button className="btn ghost" onClick={reopen}>Reopen</button>
        ) : (
          <>
            <button className="btn primary tp-act" onClick={() => complete()}>✓ Complete <kbd>E</kbd></button>
            <div className="tp-pop-anchor">
              {t.status === "waiting" ? (
                <button className="btn ghost tp-act" onClick={() => save({ status: "open" }, "Back on the list")}>Got the reply</button>
              ) : (
                <button className="btn ghost tp-act" onClick={() => setPop(pop === "waiting" ? null : "waiting")}>⏳ Waiting on… <kbd>W</kbd></button>
              )}
              {pop === "waiting" && <WaitingPop t={t} ctx={ctx} onClose={() => setPop(null)} onSave={(who, chase) => {
                setPop(null); save({ status: "waiting", waiting_on: who, chase_on: chase }, `Waiting on ${who} — back on ${fmtDate(chase)}`);
              }} />}
            </div>
            <div className="tp-pop-anchor">
              <button className="btn ghost tp-act" onClick={() => setPop(pop === "snooze" ? null : "snooze")}>Snooze <kbd>S</kbd></button>
              {pop === "snooze" && <SnoozePop onClose={() => setPop(null)} onSave={d => { setPop(null); save({ snoozed_until: d }, `Out of focus until ${fmtDate(d)}`); }} />}
            </div>
            <button className="btn ghost tp-act" onClick={() => setPop(pop === "delegate" ? null : "delegate")}>Delegate <kbd>D</kbd></button>
          </>
        )}
        <span style={{ flex: 1 }} />
        <span className="tp-nav"><button onClick={() => onStep(-1)} title="Previous task (↑)">↑</button><button onClick={() => onStep(1)} title="Next task (↓)">↓</button> next task</span>
      </div>
    </aside>
  );
}

/* ---- pieces ---- */

function WithRow({ ctx, t, contacts, onSave, onFix, onDismiss, go }: {
  ctx: Ctx; t: Todo; contacts: Contact[]; onSave: (p: Record<string, unknown>, done?: string) => void;
  onDismiss: (kind: "contact_email" | "contact_match", key: string, label: string) => void;
  onFix: (f: { action: string; payload: Record<string, unknown> }) => void; go: (s: string, id?: string) => void;
}) {
  const [adding, setAdding] = useState(false);
  const [q, setQ] = useState("");
  const options = useMemo(() => {
    const s = q.trim().toLowerCase();
    if (s.length < 2) return [];
    return contacts.filter(c => !(t.contact_ids || []).includes(c.id)
      && `${c.name} ${c.org_name || ""} ${c.email || ""}`.toLowerCase().includes(s)).slice(0, 8);
  }, [q, contacts, t.contact_ids]);
  return (
    <div className="tp-with">
      <span className="tp-k">With</span>
      <div className="tp-with-list">
        {ctx.contacts.map(c => (
          <span key={c.id} className={clsx("tp-person", !c.email && "noemail")} title={c.email || "No email address in the CRM"}>
            <button className="tp-person-n" onClick={() => go("accounts", c.id)}>{c.name}</button>
            {c.org_name && <span className="tp-person-o">{c.org_name}</span>}
            {!c.email && <span className="tp-person-flag">no email</span>}
            {!c.email && c.email_suggestions && c.email_suggestions.length > 0 && (
              <span className="tp-addr">
                {c.email_suggestions.slice(0, 2).map(g => (
                  <span key={g.email} className="tp-match-wrap">
                    <button className="tp-addr-use" title={`${g.how}${g.last_seen ? ` · last ${g.last_seen.slice(0, 10)}` : ""}${g.taken_by ? ` · also on ${g.taken_by}` : ""}`}
                      onClick={() => onFix({ action: "set_contact_email", payload: { contact_id: c.id, email: g.email, label: `${c.name}: ${g.email}` } })}>
                      use {g.email}
                    </button>
                    <button className="tp-addr-no" aria-label={`Not ${c.name}'s address`} title={`Not ${c.name}'s address — don't suggest it again`}
                      onClick={() => onDismiss("contact_email", `${c.id}:${g.email.toLowerCase()}`, `${g.email} is not ${c.name}`)}>×</button>
                  </span>
                ))}
              </span>
            )}
            <button className="tp-x" aria-label={`Remove ${c.name}`} onClick={() => onSave({ contact_ids: (t.contact_ids || []).filter(x => x !== c.id) })}>×</button>
          </span>
        ))}
        {t.with_text && !ctx.contacts.length && <span className="tp-person text">{t.with_text}</span>}
        {!ctx.contacts.length && ((t as Todo & { contact_matches?: Contact[] }).contact_matches || []).map(c => (
          <span key={c.id} className="tp-match-wrap">
            <button className="tp-match" onClick={() => onSave({ contact_ids: [c.id] }, `Linked ${c.name}`)}>
              Is this {c.name}{c.org_name ? ` (${c.org_name})` : ""}? <b>Link</b>
            </button>
            <button className="tp-addr-no" title={`Not ${c.name}`} onClick={() => onDismiss("contact_match", `${t.id}:${c.id}`, `“${t.with_text}” is not ${c.name}`)}>No</button>
          </span>
        ))}
        {adding ? (
          <span className="tp-add-person">
            <input autoFocus value={q} placeholder="Search contacts…" onChange={e => setQ(e.target.value)}
              onKeyDown={e => { if (e.key === "Escape") { setAdding(false); setQ(""); } }} />
            {options.length > 0 && (
              <span className="tp-add-list">
                {options.map(c => (
                  <button key={c.id} onClick={() => { onSave({ contact_ids: [...(t.contact_ids || []), c.id] }); setQ(""); setAdding(false); }}>
                    {c.name}<span>{c.org_name}{c.email ? "" : " · no email"}</span>
                  </button>
                ))}
              </span>
            )}
          </span>
        ) : (
          <button className="tp-mini" onClick={() => setAdding(true)}>+ contact</button>
        )}
      </div>
    </div>
  );
}

function SourceChip({ src, go }: { src: NonNullable<Ctx["source"]>; go: (s: string, id?: string) => void }) {
  const label = src.label || src.type;
  if (src.type === "email" && src.id) return <button className="tp-src" onClick={() => go("inbox", src.id!)}>✉ {label}</button>;
  if (src.type === "minutes" && src.id) return <a className="tp-src" href={`/api/minutes/${src.id}/doc`} target="_blank" rel="noreferrer">▤ {label}</a>;
  if (src.url) return <a className="tp-src" href={src.url} target="_blank" rel="noreferrer">{src.type === "radar" ? "◎ Radar: " : "↗ "}{label}</a>;
  const icon = src.type === "document" ? "▤ " : src.type === "radar" ? "◎ Radar: " : src.type === "bot" ? "✦ Added by " : "";
  return <span className="tp-src static">{icon}{label}</span>;
}

function WaitingPop({ t, ctx, onSave, onClose }: { t: Todo; ctx: Ctx; onSave: (who: string, chase: string) => void; onClose: () => void }) {
  const [who, setWho] = useState(t.waiting_on || ctx.contacts[0]?.name || t.with_text || "");
  const [chase, setChase] = useState(t.chase_on || plusDays(7));
  return (
    <div className="tp-pop up" onKeyDown={e => { if (e.key === "Escape") onClose(); }}>
      <div className="tp-pop-h">Waiting on</div>
      <input autoFocus value={who} onChange={e => setWho(e.target.value)} placeholder="Who has the ball?" />
      <div className="tp-pop-h">Bring it back on</div>
      <div className="tp-pop-row">
        {[3, 7, 14].map(n => <button key={n} className={clsx("tp-mini", chase === plusDays(n) && "on")} onClick={() => setChase(plusDays(n))}>{n} days</button>)}
        <input type="date" value={chase} onChange={e => setChase(e.target.value)} />
      </div>
      <button className="btn primary sm" disabled={!who.trim()} onClick={() => onSave(who.trim(), chase)}>Set waiting</button>
    </div>
  );
}

function SnoozePop({ onSave, onClose }: { onSave: (d: string) => void; onClose: () => void }) {
  return (
    <div className="tp-pop up" onKeyDown={e => { if (e.key === "Escape") onClose(); }}>
      <div className="tp-pop-h">Out of focus until</div>
      <button onClick={() => onSave(plusDays(1))}>Tomorrow</button>
      <button onClick={() => onSave(nextMonday())}>Next Monday</button>
      <button onClick={() => onSave(plusDays(14))}>In two weeks</button>
      <input type="date" min={plusDays(1)} onChange={e => e.target.value && onSave(e.target.value)} />
    </div>
  );
}

function SuggestedActions({ t, partners, onAccept, onDismiss }: {
  t: Todo; partners: Record<string, Partner>; onAccept: (owner: string, due: string | null) => void; onDismiss: () => void;
}) {
  const [owner, setOwner] = useState(t.owner);
  const [due, setDue] = useState(t.due || plusDays(7));
  return (
    <div className="tp-accept">
      <span className="tp-k">Take it on:</span>
      <select value={owner} onChange={e => setOwner(e.target.value)}>
        {Object.values(partners).filter(p => p.active !== false).map(p => <option key={p.id} value={p.id}>{p.name.split(" ")[0]}</option>)}
      </select>
      <input type="date" value={due || ""} onChange={e => setDue(e.target.value)} />
      <button className="btn primary sm" onClick={() => onAccept(owner, due || null)}>Accept</button>
      <button className="btn ghost sm" onClick={onDismiss}>Dismiss</button>
    </div>
  );
}

const FIELD: Record<string, string> = { owner: "owner", status: "status", due: "due date", task: "title", deal: "deal", priority: "priority", snoozed: "snoozed" };
function historyLine(h: Ctx["history"][number], partners: Record<string, Partner>): React.ReactNode {
  const who = h.by_name ? h.by_name.split(" ")[0] : h.by ? h.by.replace(/^bot:/, "🤖 ") : "someone";
  if (h.field === "created") return <>created by {who}</>;
  if (h.field === "accepted") return <>{who} accepted the suggestion</>;
  if (h.field === "owner") return <>{who} handed it from <b>{firstName(partners, h.old_value)}</b> to <b>{firstName(partners, h.new_value)}</b></>;
  if (h.field === "status") return <>{who}: {h.old_value} → <b>{h.new_value}</b></>;
  return <>{who} changed {FIELD[h.field] || h.field}{h.old_value ? <> from <i>{h.old_value.slice(0, 60)}</i></> : null} to <b>{(h.new_value || "—").slice(0, 80)}</b></>;
}

export { AvatarStack };

function HandoffBanner({ t, partners, myId, onAnswer }: {
  t: Todo; partners: Record<string, Partner>; myId: string; onAnswer: (accept: boolean, note: string | null) => void;
}) {
  const [back, setBack] = useState(false);
  const [note, setNote] = useState("");
  const from = t.delegated_by_name?.split(" ")[0] || (t.delegated_by || "").replace(/^bot:/, "") || "a colleague";
  if (t.owner !== myId) {
    return <div className="tp-handoff muted">↪ Handed to {firstName(partners, t.owner)} by {from} — waiting for them to accept</div>;
  }
  return (
    <div className="tp-handoff">
      <div>↪ <b>{from}</b> handed this to you{t.delegated_at ? ` on ${fmtDate(t.delegated_at.slice(0, 10))}` : ""}.</div>
      {back ? (
        <div className="tp-row-btns">
          <input className="tp-handoff-note" autoFocus value={note} onChange={e => setNote(e.target.value)} placeholder={`Why it should stay with ${from} (optional)`} />
          <button className="btn ghost sm" onClick={() => setBack(false)}>Cancel</button>
          <button className="btn primary sm" onClick={() => onAnswer(false, note || null)}>Hand back</button>
        </div>
      ) : (
        <div className="tp-row-btns">
          <button className="btn primary sm" onClick={() => onAnswer(true, null)}>Accept</button>
          <button className="btn ghost sm" onClick={() => setBack(true)}>Hand back…</button>
        </div>
      )}
    </div>
  );
}
